import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const UPDATED = "February 2026";
const CONTACT = "hello@myae.co.uk";

const H = ({ children }) => <h2 className="font-serif text-2xl tracking-tight mt-8 mb-2 text-foreground">{children}</h2>;
const H3 = ({ children }) => <h3 className="font-serif text-lg mt-5 mb-1 text-foreground">{children}</h3>;
const P = ({ children }) => <p className="text-[15px] leading-[1.8] text-muted-foreground mb-3">{children}</p>;
const LI = ({ children }) => <li className="text-[15px] leading-[1.8] text-muted-foreground">{children}</li>;

const ProcessorTable = () => (
  <div className="border border-border overflow-x-auto text-sm mt-2 mb-4" data-testid="processor-table">
    <table className="w-full">
      <thead className="bg-secondary">
        <tr>
          <th className="text-left px-3 py-2 font-mono text-xs">Processor</th>
          <th className="text-left px-3 py-2 font-mono text-xs">Purpose</th>
          <th className="text-left px-3 py-2 font-mono text-xs">Data</th>
          <th className="text-left px-3 py-2 font-mono text-xs">Location</th>
        </tr>
      </thead>
      <tbody className="text-[13px]">
        {[
          ["OpenAI", "AI examiner analysis + text generation", "Assignment text, brief, rubric", "US/EU (contractual no-train)"],
          ["Anthropic (Claude)", "Backup AI examiner + evaluation", "Assignment text, brief, rubric", "US (contractual no-train)"],
          ["Google Gemini", "AI examiner fallback", "Assignment text, brief, rubric", "US/EU (contractual no-train)"],
          ["Google Cloud Vision", "OCR of scanned PDFs / images", "Uploaded file content", "EU"],
          ["Brave Search", "Web-similarity signal (Submission Intelligence)", "Short excerpts (≤300 chars)", "US"],
          ["Exa AI", "Academic evidence retrieval", "Short claim excerpts", "US"],
          ["Stripe", "Payments (future paid plans)", "Name, email, billing details", "US/EU (PCI-DSS)"],
          ["Resend", "Transactional email (verification, reset, alerts)", "Name, email", "EU"],
          ["PostHog", "Product analytics (anonymised)", "Usage events, session id", "EU"],
          ["MongoDB Atlas", "Primary database (hosting)", "All account + report data", "EU"],
          ["Meta (WhatsApp)", "Operational alerts to MyAE admin only", "Incident metadata only", "US/EU"],
        ].map((row) => (
          <tr key={row[0]} className="border-t border-border">
            {row.map((cell, i) => (
              <td key={i} className="px-3 py-2 align-top">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    body: (
      <>
        <P>
          This Privacy Policy explains what personal data <b>OpenDegree Ltd, trading as MyAE</b>
          (&quot;MyAE&quot;, &quot;we&quot;, &quot;us&quot;) collects, how we use it, who we share it
          with, how long we keep it, and the rights you have under the UK GDPR / EU GDPR. By creating
          an account or using the service you agree to this policy.
        </P>
        <P>
          <b>Data controller.</b> The controller of your personal data is <b>OpenDegree Ltd, trading
          as MyAE</b>, a company registered in England and Wales (Company No. 17397971). You can
          contact us at <a className="text-oxford underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.
        </P>

        <H>1. An overview in plain English</H>
        <P>
          MyAE is an AI-tutor tool for university students. You paste or upload your assignment brief,
          marking criteria, and a draft. Our AI models produce examiner-style feedback and a
          predicted grade. To do this we store your work securely in our database and send extracts to
          trusted AI providers so they can generate the report. We do <b>not</b> sell your work, we do
          <b> not</b> allow model training on it, and we do <b>not</b> share it with your university.
        </P>

        <H>2. The information we collect</H>
        <H3>2.1 Account information</H3>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI>Name and email address when you register.</LI>
          <LI>If you sign in with Google — your Google name, email, and profile picture.</LI>
          <LI>A hashed password (bcrypt) — we never store plain-text passwords.</LI>
          <LI>Consent records — the timestamp when you accepted these Terms, and separately whether
            you opted-in to marketing email.</LI>
        </ul>

        <H3>2.2 Content you provide</H3>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI>Assignment briefs, rubrics, marking criteria and any supporting documents you upload.</LI>
          <LI>The essay / draft / group contribution text itself.</LI>
          <LI>Any grade you later confirm from your university (used only to improve our calibration
            for you, and stored anonymously for aggregate accuracy metrics).</LI>
          <LI>If you opt in to <b>Help Improve MyAE</b> — the Turnitin similarity PDF you choose to
            share so we can benchmark against real Turnitin scores.</LI>
        </ul>

        <H3>2.3 Usage &amp; technical data</H3>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI>Feature interactions (buttons clicked, reports viewed) collected through PostHog with
            an anonymised user id.</LI>
          <LI>Standard server logs (IP, user-agent, timestamps) retained only for security &amp;
            fraud-prevention and auto-purged after 30 days.</LI>
        </ul>

        <H>3. How your uploaded work is processed</H>
        <P>Transparency matters. Here is exactly what happens when you upload or paste an assignment:</P>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI><b>Extraction.</b> The text is extracted from your file (PDF, DOCX, image) locally on
            our servers.</LI>
          <LI><b>OCR (if needed).</b> Scanned PDFs or images are OCR&apos;d via <b>Google Cloud Vision</b>.
            Google acts as a data processor under standard EU model clauses.</LI>
          <LI><b>AI analysis.</b> The text is sent to one or more AI providers (OpenAI, Anthropic,
            Google Gemini) — chosen automatically by our intelligent router. All providers process
            content <b>only</b> to generate your requested report and are contractually barred from
            using it to train their models.</LI>
          <LI><b>Similarity check.</b> Our internal similarity search compares your submission against
            other documents within your own MyAE account. For the optional <i>Submission Intelligence</i>
            module, short excerpts (up to 300 chars) may be sent to <b>Brave Search</b> and <b>Exa AI</b>
            to look for external matches. Only excerpts are shared — never your full essay.</LI>
          <LI><b>Storage.</b> The extracted text and the generated report are stored in our EU-based
            MongoDB Atlas cluster so you can revisit them in your history.</LI>
          <LI><b>Deletion.</b> You can delete any analysis at any time from your history — the stored
            text and report are removed within 30 days from all backups.</LI>
        </ul>

        <H>4. Third-party processors we use</H>
        <P>We rely on a small number of trusted infrastructure and AI providers. Each is bound by a
          data-processing agreement (DPA) and only processes data under our instructions.</P>
        <ProcessorTable />

        <H>5. Marketing communications (optional)</H>
        <P>
          Marketing emails — product updates, tips and offers — are <b>only</b> sent to users who
          explicitly opted-in at sign-up or from their profile. The opt-in is unticked by default and
          stored separately from the mandatory Terms acceptance. Every marketing email includes a
          one-click unsubscribe link. Transactional emails (verification, password reset, receipts)
          are sent regardless as they are required to operate your account.
        </P>

        <H>6. Cookies &amp; local storage</H>
        <P>See our separate <Link className="text-oxford underline" to="/cookies">Cookie Policy</Link>.
          In short: we use essential local storage to keep you signed in and to link guest sessions to
          your account. We do <b>not</b> use advertising or cross-site tracking cookies.</P>

        <H>7. Your rights under UK/EU GDPR</H>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI><b>Access</b> — request a copy of the personal data we hold about you.</LI>
          <LI><b>Rectification</b> — ask us to correct inaccurate data.</LI>
          <LI><b>Erasure (&quot;right to be forgotten&quot;)</b> — ask us to delete your account and
            everything linked to it. We honour this within 30 days.</LI>
          <LI><b>Portability</b> — download your reports as PDFs from your dashboard at any time.</LI>
          <LI><b>Restriction / objection</b> — object to certain processing, including marketing.</LI>
          <LI><b>Withdraw consent</b> — withdraw marketing consent at any time from your profile.</LI>
          <LI><b>Complain</b> — you can lodge a complaint with the UK ICO
            (<a className="text-oxford underline" href="https://ico.org.uk/" target="_blank" rel="noreferrer">ico.org.uk</a>)
            or your local supervisory authority.</LI>
        </ul>
        <P>To exercise any of these rights email <a className="text-oxford underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</P>

        <H>8. Retention</H>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI><b>Account &amp; reports</b> — kept while your account is active. Deleted within 30 days
            of account deletion or an analysis being removed.</LI>
          <LI><b>Anonymised calibration data</b> — where you confirm an official grade, we may keep
            an anonymised, hashed record indefinitely to improve accuracy metrics.</LI>
          <LI><b>Server &amp; incident logs</b> — 30 days, then purged.</LI>
          <LI><b>Payment records</b> — retained by Stripe for the period required by tax /
            accounting law (typically 6 years in the UK).</LI>
        </ul>

        <H>9. International transfers</H>
        <P>Some of our providers (notably OpenAI, Anthropic, Brave, Exa, Stripe) are based in the
          United States. Where personal data is transferred outside the UK/EEA we rely on Standard
          Contractual Clauses (SCCs) and equivalent safeguards.</P>

        <H>10. Data security</H>
        <P>All traffic is encrypted with HTTPS (TLS 1.2+). Data at rest is encrypted at the storage
          layer. Passwords are hashed with bcrypt. Access to production data is restricted to
          authorised MyAE personnel only. Critical incidents are proactively monitored and paged to
          our on-call team via email and WhatsApp.</P>

        <H>11. Children</H>
        <P>The service is intended for users aged 16 and over. If we learn a younger child has
          created an account, we will delete it.</P>

        <H>12. AI limitations &amp; academic-integrity notice</H>
        <P>
          <b>MyAE provides an indicative, AI-assisted assessment. It is not an official grade and it
          is not a substitute for your institution&apos;s marking. AI outputs can contain errors,
          omissions and biases.</b> Use MyAE as a study aid, not as a submission or decision-maker.
          You remain responsible for the work you submit.
        </P>

        <H>13. Changes to this policy</H>
        <P>We may update this policy from time to time. Material changes will be announced in-app
          and by email to registered users. The &quot;Last updated&quot; date at the top of this page
          always reflects the current version.</P>

        <H>14. Contact</H>
        <P>Questions or data-subject requests? Email <a className="text-oxford underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</P>
      </>
    ),
  },
  terms: {
    title: "Terms of Service",
    body: (
      <>
        <P>
          These Terms govern your use of <b>MyAE — My Academic Edge</b> (&quot;MyAE&quot;,
          &quot;we&quot;, &quot;our&quot;), a service provided by <b>OpenDegree Ltd, trading as
          MyAE</b>, a company registered in England and Wales (Company No. 17397971). By creating an
          account or using the service you agree to these Terms. If you do not agree, please do not
          use the service.
        </P>

        <H>1. Who we are &amp; what MyAE is</H>
        <P>
          MyAE is an online AI-tutor tool that provides feedback, an indicative predicted grade,
          submission-readiness signals, and referencing / structure analysis for university
          assignments. It is a <b>study-support tool</b>. It is not an official grade, not an
          academic-integrity decision, and not a substitute for your institution&apos;s own
          marking process.
        </P>

        <H>2. Eligibility &amp; account</H>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI>You must be at least <b>16 years old</b> and legally capable of forming a binding
            contract in your country.</LI>
          <LI>You must have an active MyAE account to use the full service. Guest mode is available
            for a limited experience.</LI>
          <LI>You are responsible for the security of your login credentials and for all activity
            under your account. Notify us immediately at <a className="text-oxford underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>
            {" "}if you suspect unauthorised access.</LI>
          <LI>You must provide accurate registration information and keep it up to date.</LI>
        </ul>

        <H>3. Pricing &amp; &quot;Free until further notice&quot;</H>
        <P>
          MyAE is currently offered <b>free of charge</b> across every plan (&quot;Free until further
          notice&quot;). Prices displayed on the pricing page are our <i>future</i> prices and are
          shown with a strikethrough for transparency. When we end the free period we will:
        </P>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI>Announce the change in-app and by email at least <b>14 days</b> before it takes effect.</LI>
          <LI>Never charge you without your explicit, active consent (adding a payment method +
            confirming a plan).</LI>
          <LI>Honour any early-adopter, beta, or grandfather discount code you have redeemed.</LI>
        </ul>
        <P>Once paid plans are active, pricing, billing frequency, taxes, and cancellation terms will
          be presented to you at checkout. Payments are processed by <b>Stripe</b>. Refunds are handled
          in line with UK consumer-protection law.</P>

        <H>4. Acceptable use</H>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI>Only upload work you have the right to submit. Do not upload third-party work without
            their permission.</LI>
          <LI>Use MyAE in line with your institution&apos;s academic-integrity policy. It is
            <b> your responsibility</b> to confirm that AI-tutor tools are permitted for the
            specific assessment.</LI>
          <LI>Do not use MyAE to produce or submit AI-written work as your own — MyAE is a
            feedback tool, not a ghost-writer.</LI>
          <LI>Do not attempt to reverse-engineer, scrape, or overload the service, or bypass rate
            limits.</LI>
          <LI>No illegal, hateful, harassing, or infringing content.</LI>
        </ul>
        <P>We may suspend or terminate accounts that breach these rules.</P>

        <H>5. Ownership of your work</H>
        <P>
          You retain all rights, title, and ownership in the assignments, essays, briefs, and
          documents you upload. Uploading content to MyAE grants us <b>only</b> a limited,
          non-exclusive licence to store, process, and analyse that content for the sole purpose of
          providing the service to you. This licence does not allow us to sell, publish, redistribute
          or train third-party AI models on your work.
        </P>

        <H>6. AI limitations &amp; no guarantees on grades</H>
        <P>
          Grade predictions, similarity signals, AI-writing indicators, referencing checks, and
          submission-readiness scores are <b>estimates only</b>. They can be wrong. They must not
          be relied upon as official grades or definitive findings of academic misconduct. Your
          institution&apos;s decision always overrides MyAE.
        </P>

        <H>7. Community Training Programme</H>
        <P>
          The &quot;Help Improve MyAE&quot; programme is entirely voluntary. If you opt in, we may
          use the Turnitin similarity PDF you share (together with your MyAE submission) to
          benchmark and improve our calibration. Every consent event is logged, and you can
          revoke consent at any time from your profile — your existing contribution is then either
          anonymised or deleted at your choice.
        </P>

        <H>8. Emails we send you</H>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI><b>Transactional</b> — verification, password reset, invoices, group invites,
            security alerts. These are required to run the service.</LI>
          <LI><b>Marketing</b> — product updates, tips, offers. Only sent if you actively opted-in
            at sign-up or from your profile. One-click unsubscribe on every email.</LI>
        </ul>

        <H>9. Third-party services</H>
        <P>MyAE relies on trusted third parties (Stripe for payments, OpenAI / Anthropic / Google
          Gemini for AI, Google Cloud Vision for OCR, Brave &amp; Exa for similarity signals,
          Resend for email, PostHog for analytics, MongoDB Atlas for hosting). Their availability
          affects ours. Their handling of your data is described in our Privacy Policy.</P>

        <H>10. Availability, changes &amp; termination</H>
        <P>
          We aim for high availability but the service is provided on an &quot;as-is&quot; basis
          without uptime guarantees. We may update, suspend, or discontinue features at any time.
          We may terminate accounts for breach of these Terms. You may close your account at any
          time from your profile.
        </P>

        <H>11. Disclaimer &amp; liability</H>
        <P>To the maximum extent permitted by law:</P>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI>The service is provided <b>&quot;as is&quot;</b>, without warranties, express or
            implied, including as to accuracy, fitness for a particular purpose, or non-infringement.</LI>
          <LI>We are <b>not liable</b> for any indirect, consequential, or special loss — including
            any academic result, missed deadline, or disciplinary action arising from your use of
            MyAE outputs.</LI>
          <LI>Nothing in these Terms limits liability that cannot be limited under English law
            (e.g. death or personal injury caused by negligence, or fraud).</LI>
        </ul>

        <H>12. Indemnity</H>
        <P>You agree to indemnify MyAE against any claim arising from your breach of these Terms,
          including uploading content you did not have the right to upload.</P>

        <H>13. Changes to these Terms</H>
        <P>We may update these Terms. Material changes will be announced in-app and by email at
          least 14 days before they take effect. Continued use after that date constitutes
          acceptance.</P>

        <H>14. Governing law</H>
        <P>These Terms are governed by the laws of England &amp; Wales. The courts of England &amp;
          Wales have exclusive jurisdiction, subject to any mandatory consumer-protection rights of
          your country of residence.</P>

        <H>15. Contact</H>
        <P>Questions? Email <a className="text-oxford underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</P>
      </>
    ),
  },
  cookies: {
    title: "Cookie Policy",
    body: (
      <>
        <P>This policy explains the cookies and similar local-storage technologies MyAE uses.</P>

        <H>What we use</H>
        <ul className="list-disc pl-6 space-y-1 mb-3">
          <LI><b>Essential local storage</b> — we store a sign-in token and, for guests, an anonymous identifier
            in your browser&apos;s local storage. These are strictly necessary to keep you signed in and to link
            your examinations to you. Without them the core features will not work.</LI>
          <LI><b>Analytics (PostHog)</b> — anonymised event tracking so we can improve the product. We do
            not use it for advertising or cross-site tracking.</LI>
          <LI><b>No advertising or cross-site tracking cookies.</b></LI>
        </ul>

        <H>Third-party providers</H>
        <P>Our hosting/platform provider (Kubernetes ingress + CDN) may set essential cookies required to serve
          the site securely. Stripe may set essential cookies during a paid checkout.</P>

        <H>Managing storage</H>
        <P>You can clear your browser&apos;s cookies and local storage at any time via your browser settings.
          Doing so will sign you out and, for guests, may disconnect you from your earlier session history.</P>

        <H>Contact</H>
        <P>Questions about cookies? Email <a className="text-oxford underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</P>
      </>
    ),
  },
};

export default function Legal({ doc }) {
  const navigate = useNavigate();
  const c = CONTENT[doc] || CONTENT.privacy;
  const links = [["privacy", "Privacy Policy"], ["terms", "Terms of Service"], ["cookies", "Cookie Policy"], ["contact", "Contact"]];

  return (
    <div className="min-h-screen bg-background" data-testid={`legal-${doc}`}>
      <div className="border-b border-border bg-card px-6 md:px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-oxford hover:underline" data-testid="legal-back-btn">
          <ArrowLeft size={16} /> Back to MyAE
        </button>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-oxford flex items-center justify-center"><span className="font-serif text-white text-base leading-none">Æ</span></div>
          <span className="font-serif text-lg tracking-tight hidden sm:inline">MyAE — My Academic Edge</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <p className="overline text-oxford mb-2">Legal</p>
        <h1 className="font-serif text-5xl tracking-tight leading-none">{c.title}</h1>
        <p className="text-sm text-muted-foreground mt-2 font-mono">Last updated: {UPDATED}</p>

        <nav className="flex flex-wrap gap-x-5 gap-y-1 mt-5 mb-8 border-y border-border py-3">
          {links.map(([slug, label]) => (
            <Link key={slug} to={`/${slug}`} data-testid={`legal-link-${slug}`}
              className={`text-sm ${slug === doc ? "text-oxford font-semibold" : "text-muted-foreground hover:text-oxford"}`}>
              {label}
            </Link>
          ))}
        </nav>

        <article>{c.body}</article>

        <p className="text-xs text-muted-foreground mt-12 border-t border-border pt-4">
          MyAE — My Academic Edge — provides indicative, AI-assisted feedback and is not affiliated
          with any university. These documents are provided for transparency; please review them
          against your own legal requirements.
        </p>
      </div>
    </div>
  );
}
